// src/pages/LoginPage.js

import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useNavigate } from 'react-router-dom';
import { Box, Button, Container, TextField, Typography, Paper, Alert, Link, InputAdornment, IconButton, alpha, useTheme } from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import logo from '../assets/img/Logoifes.png'; // Import the logo

const LoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const auth = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await auth.login(username, password);
      navigate('/dashboard/select');
    } catch (err) {
      setError('Credenciales incorrectas. Por favor, intente de nuevo.');
      setLoading(false);
    }
  };

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        bgcolor: '#0a0d14',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '-20%',
          left: '-10%',
          width: '50vw',
          height: '50vw',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.15)} 0%, transparent 60%)`,
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: '-20%',
          right: '-10%',
          width: '50vw',
          height: '50vw',
          background: `radial-gradient(circle, ${alpha(theme.palette.secondary.main || '#f50057', 0.1)} 0%, transparent 60%)`,
          zIndex: 0,
        }
      }}
    >
      <Container component="main" maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        <Paper
          elevation={24}
          sx={{
            p: { xs: 4, md: 5 },
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 4,
            backdropFilter: 'blur(16px)',
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            border: `1px solid ${alpha(theme.palette.common.white, 0.05)}`,
            boxShadow: `0 24px 48px rgba(0,0,0,0.4), inset 0 1px 0 ${alpha(theme.palette.common.white, 0.1)}`
          }}
        >
          <Box sx={{ mb: 4, p: 2, borderRadius: '50%', background: alpha(theme.palette.common.white, 0.03), border: `1px solid ${alpha(theme.palette.common.white, 0.05)}` }}>
            <img src={logo} alt="Company Logo" style={{ maxWidth: '140px', height: 'auto', filter: 'drop-shadow(0 4px 8px rgba(0,0,0,0.3))' }} />
          </Box>

          <Typography component="h1" variant="h5" fontWeight={800} sx={{ mb: 1, letterSpacing: -0.5 }}>
            CRM Educativo
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Acceso al panel de gestión y notificaciones
          </Typography>

          <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="username"
              placeholder="Usuario"
              name="username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <PersonOutlineIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.1) },
                  '& input:-webkit-autofill': {
                    transition: 'background-color 5000s ease-in-out 0s',
                    WebkitTextFillColor: '#fff !important',
                  }
                }
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              placeholder="Contraseña"
              type={showPassword ? 'text' : 'password'}
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleClickShowPassword}
                      edge="end"
                      size="small"
                      sx={{ color: 'text.secondary' }}
                    >
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.background.default, 0.5),
                  '& fieldset': { borderColor: alpha(theme.palette.common.white, 0.1) },
                  '& input:-webkit-autofill': {
                    transition: 'background-color 5000s ease-in-out 0s',
                    WebkitTextFillColor: '#fff !important',
                  }
                }
              }}
            />

            {error && (
              <Alert 
                severity="error" 
                variant="filled" 
                sx={{ 
                  mt: 3, 
                  borderRadius: 2, 
                  bgcolor: alpha(theme.palette.error.dark, 0.5),
                  color: 'error.contrastText',
                  border: `1px solid ${alpha(theme.palette.error.main, 0.3)}`
                }}
              >
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !username || !password}
              sx={{
                mt: 4,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                fontWeight: 700,
                textTransform: 'none',
                fontSize: '1rem',
                boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.25)}`,
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.35)}`,
                }
              }}
            >
              {loading ? 'Ingresando...' : 'Acceder al CRM'}
            </Button>
          </Box>

          <Box sx={{ mt: 5, pt: 3, borderTop: `1px solid ${alpha(theme.palette.common.white, 0.05)}`, width: '100%', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: alpha(theme.palette.common.white, 0.5), fontSize: '0.75rem', letterSpacing: 0.5 }}>
              powered by{' '}
              <Link 
                href="https://www.zeron.com.ar" 
                target="_blank" 
                rel="noopener noreferrer" 
                sx={{ 
                  fontWeight: 800, 
                  color: alpha(theme.palette.common.white, 0.8), 
                  textDecoration: 'none', 
                  letterSpacing: 1,
                  transition: 'color 0.2s ease',
                  '&:hover': { 
                    color: theme.palette.primary.light 
                  } 
                }}
              >
                ZeRoN
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;
